import { Info } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useFeeRates } from "@/hooks/use-fee-rates";

interface PayoutBreakdownProps {
  session: {
    priceTotal?: number | null;
    platformFee?: number | null;
    gymRevenue?: number | null;
    splitSnapshot?: number | null;
    trainerPayout?: number | null;
  };
  className?: string;
}

function fmt(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function PayoutBreakdown({ session, className }: PayoutBreakdownProps) {
  const { data: feeRates } = useFeeRates();

  const priceTotal = session.priceTotal ?? 0;
  const bookingFee = session.platformFee ?? 0;
  const gymRevenue = session.gymRevenue ?? 0;
  const splitSnapshot = session.splitSnapshot ?? 0;
  const trainerPayout = session.trainerPayout ?? 0;

  if (!priceTotal) {
    return (
      <span className={className ?? "text-primary font-semibold font-mono"}>
        {fmt(trainerPayout)}
      </span>
    );
  }

  const platformTakeRate = feeRates?.platformTakeRate ?? null;
  const ccRate = feeRates?.ccRate ?? null;
  const platformCut = platformTakeRate !== null ? priceTotal * platformTakeRate : null;
  const ccFee = ccRate !== null ? priceTotal * ccRate : null;
  const platformPct = platformTakeRate !== null ? `${Math.round(platformTakeRate * 100)}%` : "…";
  const ccPct = ccRate !== null ? `${(ccRate * 100).toFixed(2)}%` : "…";

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={
            className ??
            "inline-flex items-center gap-1 text-primary font-semibold font-mono hover:opacity-80 transition-opacity cursor-pointer"
          }
          onClick={e => e.stopPropagation()}
        >
          {fmt(trainerPayout)}
          <Info className="w-3 h-3 text-muted-foreground shrink-0" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-60 p-0 bg-card border border-white/10 shadow-xl"
        align="start"
        side="top"
      >
        <div className="px-4 py-2.5 border-b border-white/10">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Payout Breakdown
          </p>
        </div>
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total charged</span>
            <span className="text-white font-mono">{fmt(priceTotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Platform cut ({platformPct})</span>
            <span className="text-red-400 font-mono">
              {platformCut !== null ? `−${fmt(platformCut)}` : "…"}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">CC processing ({ccPct})</span>
            <span className="text-red-400 font-mono">
              {ccFee !== null ? `−${fmt(ccFee)}` : "…"}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Booking fee</span>
            <span className={bookingFee > 0 ? "text-red-400 font-mono" : "text-muted-foreground font-mono"}>
              {bookingFee > 0 ? `−${fmt(bookingFee)}` : fmt(0)}
            </span>
          </div>
          {gymRevenue > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Gym split{splitSnapshot > 0 ? ` (${splitSnapshot}%)` : ""}
              </span>
              <span className="text-red-400 font-mono">−{fmt(gymRevenue)}</span>
            </div>
          )}
          <div className="pt-2 border-t border-white/10 flex items-center justify-between">
            <span className="text-sm font-semibold text-white">Your payout</span>
            <span className="text-primary font-mono font-bold">{fmt(trainerPayout)}</span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
